class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.requestVersion = 0;
    this.discoveryPanel = this.querySelector('.search-empty');

    this.setupEventListeners();
    if (this.discoveryPanel) {
      this.setAttribute('data-search-view', 'empty');
      this.input.addEventListener('input', () => this.prepareDiscovery());
    }
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  prepareDiscovery() {
    this.requestVersion++;
    this.abortController.abort();
    this.abortController = new AbortController();
    this.toggleResetButton();
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', 'false');
    this.input.removeAttribute('aria-activedescendant');

    if (!this.getQuery()) {
      this.searchTerm = '';
      this.close(true);
      return;
    }

    // Hold the visible panel through the debounce and network request.
    const visiblePanel = this.hasAttribute('open')
      ? this.predictiveSearchResults
      : this.discoveryPanel;
    const height = visiblePanel.getBoundingClientRect().height;
    if (height) this.predictiveSearchResults.style.minHeight = `${height}px`;
    this.setAttribute('data-search-pending', '');
    this.predictiveSearchResults.inert = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.predictiveSearchResults.setAttribute('aria-busy', 'true');
    this.setLiveRegionLoadingState();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.discoveryPanel) {
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      }
      this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    }
    this.searchTerm = newSearchTerm;
    if (!newSearchTerm) {
      this.close(true);
      return;
    }
    // Do not let the delayed input callback reopen a dismissed header modal.
    const details = this.closest('details');
    if (details && !details.open) return;
    if (!details && !this.contains(document.activeElement)) return;
    this.getSearchResults(newSearchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const query = this.getQuery();
    if (!query) {
      if (this.discoveryPanel) this.close(true);
      return;
    }
    this.searchTerm = query;
    if (this.discoveryPanel) this.prepareDiscovery();
    this.getSearchResults(query);
  }

  onFocusOut() {
    setTimeout(() => {
      const details = this.closest('details');
      // The header modal owns dismissal; moving focus must not hide results.
      if (details?.open) return;
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (previousTerm && currentButtonText.split(previousTerm).length > 2) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open') || this.hasAttribute('data-search-pending')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    if (!allVisibleElements.length) return;
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    if (this.hasAttribute('data-search-pending')) return;
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  async getSearchResults(searchTerm) {
    this.abortController.abort();
    this.abortController = new AbortController();
    const version = ++this.requestVersion;
    const queryKey = searchTerm.toLowerCase();
    const isCurrent = () => {
      const details = this.closest('details');
      return version === this.requestVersion &&
        this.getQuery() === searchTerm &&
        (details ? details.open : this.contains(document.activeElement));
    };
    this.setLiveRegionLoadingState();

    try {
      let markup = this.cachedResults[queryKey];
      if (!markup) {
        const response = await fetch(
          `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
          { signal: this.abortController.signal }
        );
        if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
        const html = await response.text();
        if (!isCurrent()) return;
        const section = new DOMParser().parseFromString(html, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Search response was missing its section.');
        markup = section.innerHTML;
        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = markup;
        });
      }
      if (isCurrent()) this.renderSearchResults(markup);
    } catch (error) {
      if (error.name === 'AbortError' || !isCurrent()) return;
      this.close();
      if (this.discoveryPanel) this.setAttribute('data-search-view', 'empty');
      this.setLiveRegionText('Suggestions could not load. Press Enter to search.');
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (this.discoveryPanel) {
      this.setAttribute('data-search-view', 'results');
      this.removeAttribute('data-search-pending');
      this.predictiveSearchResults.inert = false;
      this.predictiveSearchResults.removeAttribute('aria-busy');
    }
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    this.requestVersion++;
    this.abortController.abort();
    this.abortController = new AbortController();
    this.removeAttribute('data-search-pending');
    this.predictiveSearchResults.inert = false;
    this.predictiveSearchResults.removeAttribute('aria-busy');
    if (clearSearchTerm && this.discoveryPanel) {
      this.setAttribute('data-search-view', 'empty');
    }
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
